import { useRef, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Banknote, FileText, Receipt, Truck, Upload } from "lucide-react";
import {
  ORDER_ROUTES,
  PROOF_MAX_BYTES,
  formatPrice,
  paySaleResponseSchema,
  proofMimeSchema,
  type Order,
  type OrderProofInput,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { PageHeader } from "@/shared/ui/layout";
import { Alert, Button } from "@/shared/ui/core";
import { EmptyState, Modal, SkeletonTable } from "@/shared/ui/ui";
import { fileToBase64 } from "@/features/kyc/kyc.api";
import {
  DataTable,
  DataTableFrame,
  Pagination,
  Td,
  Th,
  Tr,
  DEFAULT_PER_PAGE,
} from "@/features/admin/components";
import { OrderStatusBadge } from "@/features/admin/orders/OrderStatusBadge";
import { formatOrderDate } from "@/features/admin/orders/format";
import { useAuth } from "@/features/auth/auth.context";
import { useMyOrders } from "./minegocio.api";


/**
 * Mis órdenes, en dos pestañas: lo que YO compré y lo que VENDIÓ mi
 * tienda personalizada. La misma tabla — el papel cambia, la lectura no.
 *
 * La pestaña de VENTAS solo existe para franquiciatarios (Anahí,
 * 27-ago): un socio no tiene tienda, así que enseñarle "ventas de tu
 * tienda" es prometerle algo que su rol no incluye.
 */
export function OrdenesPage() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [role, setRole] = useState<"buyer" | "seller">("buyer");
  const [page, setPage] = useState(1);
  /*
   * Las filas por página las elige quien mira (Anahí, 29-ago): 5, 10 o 20,
   * con CINCO por defecto. Vive en el estado y no como constante porque
   * ahora cambia en caliente; al cambiarlo se vuelve a la página 1, que es
   * lo único sensato — la página 4 de 5 en 5 no es la página 4 de 20 en 20.
   */
  const [perPage, setPerPage] = useState<number>(DEFAULT_PER_PAGE);
  const [cobrando, setCobrando] = useState<Order | null>(null);
  const hasStore = Boolean(user?.franchiseeSince);

  const orders = useMyOrders({ page, perPage: perPage, role });
  const items = orders.data?.items ?? [];
  const total = orders.data?.total ?? 0;

  function switchRole(next: "buyer" | "seller") {
    setRole(next);
    setPage(1);
  }

  return (
    <>
      <PageHeader
        titleHidden
        title={t("Mis órdenes")}
        description={hasStore ? t("Tus compras y las ventas de tu tienda.") : t("Tus compras en It's The Best.")}
      />

      {hasStore && (
        <div className="mb-4 flex gap-2">
          <Button
            size="sm"
            variant={role === "buyer" ? "primary" : "secondary"}
            onClick={() => switchRole("buyer")}
          >
            {t("Mis compras")}
          </Button>
          <Button
            size="sm"
            variant={role === "seller" ? "primary" : "secondary"}
            onClick={() => switchRole("seller")}
          >
            {t("Ventas de mi tienda")}
          </Button>
        </div>
      )}

      <DataTableFrame aria-busy={orders.isFetching}>
        {orders.isPending ? (
          <div className="p-5">
            <SkeletonTable rows={5} columns={5} />
          </div>
        ) : items.length === 0 ? (
          <div className="p-4">
            <EmptyState
              icon={<Receipt className="h-5 w-5 text-ds-ink-3" />}
              title={role === "buyer" ? t("Todavía no has comprado") : t("Todavía no hay ventas")}
              description={
                role === "buyer"
                  ? t("Tus pedidos en It's The Best aparecerán aquí.")
                  : t("Cada pedido hecho en tu tienda personalizada aparecerá aquí.")
              }
            />
          </div>
        ) : (
          <DataTable caption={t("Mis órdenes")}>
            <thead>
              <tr>
                <Th>{t("Folio")}</Th>
                <Th>{role === "buyer" ? t("Artículos") : t("Cliente")}</Th>
                <Th align="right">{t("Total")}</Th>
                <Th align="center">{t("Estado")}</Th>
                <Th align="right">{t("Fecha")}</Th>
                {role === "seller" && <Th align="right">{t("Acción")}</Th>}
              </tr>
            </thead>
            <tbody>
              {items.map((order) => (
                <Tr key={order.id}>
                  <Td className="font-mono text-[13px]">{order.folio}</Td>
                  <Td>
                    {role === "buyer"
                      ? order.items.map((item) => `${item.quantity}× ${item.name}`).join(" · ")
                      : order.customer.name}
                  </Td>
                  <Td align="right" className="font-medium">
                    {formatPrice(order.totalCents, order.currency)}
                  </Td>
                  <Td align="center">
                    <OrderStatusBadge status={order.status} />
                    {/* La guía de Trasso, cuando ya existe: paquetería y
                        número de rastreo, para seguir el paquete. */}
                    {order.shipping?.trackingNumber &&
                      order.shipping.status !== "cancelled" && (
                        <span className="mt-1 flex items-center justify-center gap-1 text-[11px] text-ds-ink-3">
                          <Truck className="h-3 w-3" />
                          {order.shipping.carrier}{" "}
                          <span className="font-mono">{order.shipping.trackingNumber}</span>
                        </span>
                      )}
                  </Td>
                  <Td align="right" className="text-ds-ink-3">
                    {formatOrderDate(order.createdAt)}
                  </Td>
                  {role === "seller" && (
                    <Td align="right">
                      {order.status === "pending" ? (
                        <Button
                          size="sm"
                          variant="secondary"
                          leftIcon={<Banknote />}
                          onClick={() => setCobrando(order)}
                        >
                          {t("Registrar pago")}
                        </Button>
                      ) : (
                        <span className="text-[12px] text-ds-ink-3">—</span>
                      )}
                    </Td>
                  )}
                </Tr>
              ))}
            </tbody>
          </DataTable>
        )}

        {total > 0 && (
          <Pagination page={page} perPage={perPage} total={total} onPageChange={setPage}
            onPerPageChange={(n) => {
              setPerPage(n);
              setPage(1);
            }}
          />
        )}
      </DataTableFrame>

      <RegistrarPagoModal
        venta={cobrando}
        onClose={() => setCobrando(null)}
      />
    </>
  );
}

/**
 * ═══════════════════════════════════════════════════════════════════
 * Registrar el pago de UNA venta (Anahí, 27-ago): el franquiciatario
 * confirma que su cliente ya le pagó —con el comprobante si lo tiene—
 * y HASTA este acto la venta se valida: se descuenta el stock de su
 * vitrina y entra a la contabilidad, por el mismo camino que la
 * validación del panel.
 * ═══════════════════════════════════════════════════════════════════
 */
function RegistrarPagoModal({ venta, onClose }: { venta: Order | null; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const inputRef = useRef<HTMLInputElement>(null);
  const [proof, setProof] = useState<OrderProofInput | null>(null);
  const [error, setError] = useState<string | null>(null);

  const pagar = useMutation({
    mutationFn: () =>
      authFetch(ORDER_ROUTES.paySale(venta?.id ?? ""), paySaleResponseSchema, {
        baseUrl: "",
        method: "POST",
        body: proof ? { proof } : {},
      }),
    onSuccess: () => {
      setProof(null);
      onClose();
      // La venta validada cambia TODO: pedidos, stock de la tienda y dashboard.
      void queryClient.invalidateQueries({ queryKey: ["mi-negocio"] });
    },
    onError: (cause) =>
      setError(cause instanceof Error ? cause.message : t("No se pudo registrar el pago")),
  });

  async function onFile(file: File | undefined) {
    setError(null);
    if (!file) return;
    const mime = proofMimeSchema.safeParse(file.type);
    if (!mime.success) {
      setError(t("Solo se aceptan fotos (JPG, PNG, WebP) o PDF."));
      return;
    }
    if (file.size > PROOF_MAX_BYTES) {
      setError(t("El comprobante no puede pasar de 4 MB."));
      return;
    }
    setProof({ fileName: file.name, mimeType: mime.data, dataBase64: await fileToBase64(file) });
  }

  return (
    <Modal
      open={venta !== null}
      onClose={onClose}
      title={venta ? t("Registrar el pago de {{folio}}", { folio: venta.folio }) : ""}
    >
      {venta && (
        <div className="grid gap-4">
          <p className="text-[14px] leading-relaxed text-ds-ink-3">
            {t("Confirmas que {{name}} ya te pagó", { name: venta.customer.name })}{" "}
            <span className="font-mono font-semibold text-ds-ink">
              {formatPrice(venta.totalCents, venta.currency)}
            </span>
            . {t("Al registrarlo, la venta se valida y el stock se descuenta de tu vitrina.")}
          </p>

          <div className="flex flex-wrap items-center gap-3 rounded-2xl border border-ds-border bg-ds-cream-2/40 px-4 py-3">
            <input
              ref={inputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,application/pdf"
              className="hidden"
              onChange={(event) => {
                void onFile(event.target.files?.[0]);
                event.target.value = "";
              }}
            />
            <FileText className="h-4 w-4 shrink-0 text-ds-ink-3" />
            <span className="min-w-0 flex-1 truncate text-[13px] text-ds-ink-3">
              {proof ? proof.fileName : t("Comprobante del pago (recomendado)")}
            </span>
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Upload />}
              onClick={() => inputRef.current?.click()}
            >
              {proof ? t("Cambiar") : t("Subir")}
            </Button>
          </div>

          {error && (
            <Alert variant="error" onDismiss={() => setError(null)}>
              {error}
            </Alert>
          )}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="ghost" onClick={onClose}>
              {t("Cancelar")}
            </Button>
            <Button leftIcon={<Banknote />} loading={pagar.isPending} onClick={() => pagar.mutate()}>
              {t("Registrar el pago")}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
}
